<template>
  <BaseModal
    class="infoauto-finder-modal"
    :value="true"
    @input="$emit('close')"
    :allowClose="true"
    :showHeader="false"
  >
    <InfoautoCreateCustomerModal
      v-if="showCreateInfoautoCustomer"
      @close="showCreateInfoautoCustomer = false"
      @newCustomer="createdCustomer"
    />
    <div class="finder">
      <div class="finder-header">
        <h2 class="finder-title">
          <i class="mdi mdi-account-search"></i>
          {{ $t("message.infoauto_customer_finder.title") }}
        </h2>
        <button
          v-if="showCreateCustomer"
          type="button"
          class="btn-dark-button finder-create"
          @click="showCreateInfoautoCustomer = true"
        >
          <i class="mdi mdi-account-plus"></i>
          {{ $t("message.infoauto_customer_finder.create_customer") }}
        </button>
      </div>

      <!-- Queda fija arriba mientras se hace scroll por los resultados -->
      <div class="finder-search">
        <div class="search-box">
          <i class="mdi mdi-magnify search-icon"></i>
          <input
            ref="search"
            type="text"
            class="search-input"
            autocomplete="off"
            v-model="search"
            :placeholder="
              $t('message.infoauto_customer_finder.search_placeholder')
            "
            @input="onSearchInput"
            @keydown.down.prevent="moveHighlight(1)"
            @keydown.up.prevent="moveHighlight(-1)"
            @keydown.enter.prevent="selectHighlighted"
          />
          <BaseMiniSpinner v-if="loading" class="search-spinner" />
          <button
            v-else-if="search"
            type="button"
            class="search-clear"
            :title="$t('message.infoauto_customer_finder.clear_search')"
            @click="clearSearch"
          >
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <div v-if="results.length && !error" class="search-meta">
          <span>
            {{
              $tc(
                "message.infoauto_customer_finder.results_count",
                results.length,
                { count: results.length }
              )
            }}
          </span>
          <span class="keyboard-hint">
            {{ $t("message.infoauto_customer_finder.keyboard_hint") }}
          </span>
        </div>
      </div>

      <div class="finder-results" :class="{ 'is-loading': loading }">
        <div v-if="error" class="finder-error">
          <i class="mdi mdi-alert-circle-outline"></i>
          <span>{{ error }}</span>
        </div>

        <template v-else-if="results.length">
          <!-- Escritorio: tabla navegable con flechas -->
          <div class="results-table-wrap">
            <table class="results-table">
              <thead>
                <tr>
                  <th>
                    {{ $t("message.infoauto_customer_finder.infoauto_id") }}
                  </th>
                  <th>{{ $t("message.infoauto_customer_finder.name") }}</th>
                  <th>{{ $t("message.infoauto_customer_finder.email") }}</th>
                  <th>{{ $t("message.infoauto_customer_finder.phone") }}</th>
                  <th>
                    {{ $t("message.infoauto_customer_finder.price_group") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(result, index) in results"
                  :key="result.personaId"
                  :data-result-index="index"
                  :class="{ 'is-highlighted': index === highlightedIndex }"
                  @click="select(result)"
                >
                  <td class="cell-id">{{ result.personaId }}</td>
                  <td class="cell-name">{{ result.nombreComp }}</td>
                  <td>
                    <span v-if="result.email1">{{ result.email1 }}</span>
                    <span v-else class="empty-value">—</span>
                  </td>
                  <td>
                    <template v-if="hasPhones(result)">
                      <div
                        v-for="telefono in result.telefono"
                        :key="telefono.telefonoId"
                      >
                        {{ telefono.numero }}
                      </div>
                    </template>
                    <span v-else class="empty-value">—</span>
                  </td>
                  <td>
                    <span v-if="result.grupoPrecio" class="price-chip">
                      {{ getPriceLevelName(result.grupoPrecio) }}
                    </span>
                    <span v-else class="empty-value">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Movil: tarjetas -->
          <div class="results-cards">
            <button
              v-for="result in results"
              :key="result.personaId"
              type="button"
              class="result-card"
              @click="select(result)"
            >
              <div class="card-head">
                <span class="card-name">{{ result.nombreComp }}</span>
                <span class="card-id">ID {{ result.personaId }}</span>
              </div>
              <div v-if="result.email1" class="card-line">
                <i class="mdi mdi-email-outline"></i>
                <span>{{ result.email1 }}</span>
              </div>
              <div
                v-for="telefono in result.telefono"
                :key="telefono.telefonoId"
                class="card-line"
              >
                <i class="mdi mdi-phone-outline"></i>
                <span>{{ telefono.numero }}</span>
              </div>
              <span v-if="result.grupoPrecio" class="price-chip">
                {{ getPriceLevelName(result.grupoPrecio) }}
              </span>
            </button>
          </div>
        </template>

        <div v-else class="finder-empty">
          <i
            class="mdi"
            :class="
              showNoResults
                ? 'mdi-account-question-outline'
                : 'mdi-card-search-outline'
            "
          ></i>
          <p>
            {{
              showNoResults
                ? $t("message.infoauto_customer_finder.no_results")
                : $t("message.infoauto_customer_finder.empty_state")
            }}
          </p>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
<script>
import { debounce } from "lodash";
import { infoautoSearchCustomer } from "@/api/store/Infoauto.js";
import InfoautoCreateCustomerModal from "@/components/customer/InfoautoCreateCustomerModal.vue";

export default {
  name: "InfoautoFinder",
  components: {
    InfoautoCreateCustomerModal
  },
  props: {
    showCreateCustomer: {
      type: Boolean,
      default: true
    },
    departamentoId: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      error: null,
      search: "",
      results: [],
      hasSearched: false,
      requestId: 0,
      highlightedIndex: 0,
      showCreateInfoautoCustomer: false,
      loading: false
    };
  },
  computed: {
    priceLevels() {
      return this.$store.getters["general/getPriceLevels"];
    },
    showNoResults() {
      // createdCustomer puede dejar un personaId numerico en search
      return this.hasSearched && !!String(this.search).trim();
    }
  },
  created() {
    // Una busqueda cuando se deja de escribir, no una por tecla
    this.debouncedLoadCustomers = debounce(this.loadCustomers, 300);
  },
  mounted() {
    // BaseModal solo enfoca al cambiar su value y aqui siempre es true
    this.$nextTick(() => {
      if (this.$refs.search) this.$refs.search.focus();
    });
  },
  beforeDestroy() {
    this.debouncedLoadCustomers.cancel();
  },
  methods: {
    getPriceLevelName(grupoPrecioId) {
      if (!grupoPrecioId) return "N/A";
      const priceLevel = (this.priceLevels || []).find(
        level => level.grupoPrecioId === grupoPrecioId
      );
      return priceLevel ? priceLevel.descripcion : grupoPrecioId;
    },
    hasPhones(result) {
      return Array.isArray(result.telefono) && result.telefono.length > 0;
    },
    select(result) {
      this.$emit("selectedCustomer", result);
    },
    createdCustomer(newCustomer) {
      this.search = newCustomer.personaId;
      this.loadCustomers();
      this.$emit("selectedCustomer", newCustomer);
    },
    onSearchInput() {
      if (!String(this.search).trim()) {
        this.clearResults();
        return;
      }
      this.loading = true;
      this.debouncedLoadCustomers();
    },
    clearSearch() {
      this.search = "";
      this.clearResults();
      this.$refs.search.focus();
    },
    clearResults() {
      this.debouncedLoadCustomers.cancel();
      // Invalida la respuesta que este en camino
      this.requestId++;
      this.results = [];
      this.error = null;
      this.hasSearched = false;
      this.loading = false;
    },
    moveHighlight(step) {
      if (!this.results.length) return;
      const last = this.results.length - 1;
      this.highlightedIndex = Math.min(
        Math.max(this.highlightedIndex + step, 0),
        last
      );
      this.$nextTick(() => {
        const row = this.$el.querySelector(
          `[data-result-index="${this.highlightedIndex}"]`
        );
        if (row) row.scrollIntoView({ block: "nearest" });
      });
    },
    selectHighlighted() {
      // Mientras carga, los resultados en pantalla son de la busqueda anterior
      if (this.loading) return;
      const result = this.results[this.highlightedIndex];
      if (result) this.select(result);
    },
    loadCustomers() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = null;
      infoautoSearchCustomer(this.search, this.departamentoId)
        .then(res => {
          if (requestId !== this.requestId) return;
          this.results = Array.isArray(res.data) ? res.data : [];
          this.highlightedIndex = 0;
          this.hasSearched = true;
        })
        .catch(e => {
          if (requestId !== this.requestId) return;
          this.error =
            e?.response?.data?.error?.message ||
            e?.message ||
            "Error on request";
          this.results = [];
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false;
        });
    }
  }
};
</script>
<style lang="scss">
.content {
  width: 100%;
}

// Dialogo centrado: sin esto BaseModal lo dejaba como una franja de todo el
// ancho pegada arriba, encima de la orden
.infoauto-finder-modal > .modal-content {
  width: calc(100% - 32px);
  max-width: 960px;
  min-height: 0;
  max-height: calc(100vh - 160px);
  border-radius: 12px;
  box-shadow: 0 20px 48px -12px rgba(0, 0, 0, 0.45);

  @media (max-width: 639px) {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
}
</style>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

$border: #e5e7eb;
$muted: #6b7280;
$text: #111827;
$accent-soft: rgba(18, 110, 106, 0.08);

.finder {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: 16px 20px 20px;

  @media (max-width: 767px) {
    padding: 12px;
  }
}

.finder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  // Deja libre la X de BaseModal (absoluta arriba a la derecha)
  padding-right: 36px;
  margin-bottom: 12px;
}

.finder-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: $text;

  i {
    font-size: 24px;
    color: $link-color-1;
  }

  @media (max-width: 767px) {
    font-size: 18px;
  }
}

.finder-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 18px;
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
  }
}

.finder-search {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 4px 0 10px;
  background: #fefefe;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 12px;
    font-size: 20px;
    color: $muted;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    height: 46px;
    padding: 0 44px 0 42px;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    background: white;
    font-size: 15px;
    color: $text;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: $link-color-1;
      box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.12);
    }

    // 16px evita el zoom automatico de iOS al enfocar
    @media (max-width: 767px) {
      font-size: 16px;
    }
  }

  .search-spinner {
    position: absolute;
    right: 14px;
    width: 18px;
    height: 18px;
    border-top-color: $link-color-1;
  }

  .search-clear {
    position: absolute;
    right: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    color: $muted;

    &:hover {
      background: #f3f4f6;
      color: $text;
    }

    i {
      font-size: 18px;
    }
  }
}

.search-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: $muted;

  .keyboard-hint {
    @media (max-width: 767px) {
      display: none;
    }
  }
}

.finder-results {
  transition: opacity 0.15s;

  &.is-loading {
    opacity: 0.6;
  }
}

.results-table-wrap {
  overflow-x: auto;
  border: 1px solid $border;
  border-radius: 10px;

  @media (max-width: 767px) {
    display: none;
  }
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: #374151;

  th {
    padding: 10px 12px;
    background: #f9fafb;
    border-bottom: 1px solid $border;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-align: left;
    text-transform: uppercase;
    color: $muted;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.1s;

    &:last-child td {
      border-bottom: none;
    }

    &:hover {
      background: #f9fafb;
    }

    &.is-highlighted {
      background: $accent-soft;
      box-shadow: inset 3px 0 0 $link-color-1;
    }
  }

  .cell-id {
    color: $muted;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .cell-name {
    font-weight: 600;
    color: $text;
  }
}

.results-cards {
  display: none;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.result-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 12px;
  border: 1px solid $border;
  border-radius: 10px;
  background: white;
  text-align: left;

  &:active {
    background: $accent-soft;
    border-color: $link-color-1;
  }

  .card-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    margin-bottom: 2px;
  }

  .card-name {
    font-size: 15px;
    font-weight: 600;
    color: $text;
  }

  .card-id {
    flex-shrink: 0;
    font-size: 12px;
    color: $muted;
  }

  .card-line {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #374151;
    word-break: break-word;

    i {
      font-size: 16px;
      color: $muted;
    }
  }

  .price-chip {
    margin-top: 4px;
  }
}

.price-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: $accent-soft;
  color: $link-color-1;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.empty-value {
  color: #d1d5db;
}

.finder-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 16px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #f9fafb;
  text-align: center;
  color: $muted;

  i {
    font-size: 36px;
    color: #9ca3af;
  }

  p {
    max-width: 420px;
    margin: 0;
    font-size: 14px;
  }
}

.finder-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  font-size: 14px;
  font-weight: 500;
  color: #b91c1c;

  i {
    font-size: 20px;
  }
}
</style>
